import { withKeycloak } from "@react-keycloak/web";
import React from "react";
import { labels } from "../../../../../../../localization/labels";
import { messages } from "../../../../../../../localization/messages";
import ClassificationService from "../../../../../../../services/ClassificationService";
import ClassificationStatusService from "../../../../../../../services/ClassificationStatusService";
import ClassificationTypeService from "../../../../../../../services/ClassificationTypeService";
import { activeFilterPageInfo } from "../../../../../../../services/FilteringUtils";
import CommonUtil from "../../../../../../../utils/CommonUtil";
import PathUtil from "../../../../../../../utils/PathUtil";
import {
  classificationValidationRules as validationRules,
  validateEntity
} from "../../../../../../../validators/validationRules";
import { paths } from "../../../../../../paths";
import SubControlComponent from "../../../../../page/SubControlComponent/SubControlComponent";
import ClassificationDetail from "./ClassificationDetail";

class ClassificationDetailContainer extends SubControlComponent {
  constructor(props) {
    super(props);

    this.classificationTypeService = new ClassificationTypeService(
      this.keycloak
    );
    this.classificationStatusService = new ClassificationStatusService(
      this.keycloak
    );
    this.classificationService = new ClassificationService(this.keycloak);

    this.maxLength = {
      name: 255,
      description: 2000,
      code: 60
    };

    this.state = {
      id: this.resolveItemId(),
      isChanged: false
    };
    this.state = this.initialState(this.state);
  }

  initialState = state => {
    return {
      ...state,
      entity: {},
      oldEntity: undefined,
      disabled: true,
      statuses: [],
      types: [],
      possibleParents: []
    };
  };

  onStatusLoad = data => {
    const dataContent = CommonUtil.getDataContent(data);
    if (dataContent) {
      this.setEntityProperty("classificationStatus", dataContent[0], true);
    }
  };

  loadEntityStatus = entity => {
    const url = entity._links.classificationStatuses.href;
    this.prepareCall(
      this.classificationStatusService.getEntitiesByUrl(
        url,
        this.onStatusLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onParentLoad = data => {
    const dataContent = CommonUtil.getDataContent(data);
    if (dataContent) {
      this.setEntityProperty("parent", dataContent[0], true);
    }
  };

  loadEntityParent = entity => {
    const url = entity._links.parent.href;
    this.prepareCall(
      this.classificationService.getEntitiesWithParams(
        url,
        undefined,
        undefined,
        this.onParentLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  onClassificationsLoad = data => {
    const dataContent = CommonUtil.getDataContent(data);
    const possibleParents = dataContent
      ? dataContent.filter(item => item.id !== this.state.entity.id)
      : [];
    this.setState({
      possibleParents: possibleParents
    });
  };

  loadClassificationsByType = type => {
    if (type) {
      this.prepareCall(
        this.classificationService.getEntitiesWithParams(
          type._links.classifications.href,
          activeFilterPageInfo,
          undefined,
          this.onClassificationsLoad,
          this.messageHandler.handleError
        )
      );
      this.performCalls();
    }
  };

  onTypeLoad = data => {
    const dataContent = CommonUtil.getDataContent(data);
    if (dataContent) {
      const type = dataContent[0];
      this.setEntityProperty("classificationType", type, true);
      this.loadClassificationsByType(type);
    }
  };

  loadEntityType = entity => {
    const url = entity._links.classificationTypes.href;
    this.prepareCall(
      this.classificationTypeService.getEntitiesByUrl(
        url,
        this.onTypeLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  handleOnDelete = () => {
    const confirmed = window.confirm(messages.delete_selected_confirm_message);
    if (confirmed) {
      this.onDeleteData(this.state.entity);
    }
  };

  onDeleteLoad = () => {
    const path = PathUtil.adminPath(paths.CLASSIFICATIONS);
    this.changePath(path);
  };

  onDeleteData = selected => {
    this.prepareCall(
      this.classificationService.deleteEntity(
        selected,
        this.onDeleteLoad,
        this.onDeleteSuccessMessage,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  handleDialogChange = event => {
    const { entity } = this.state;
    entity[event.target.id] = event.target.value;
    const disabled = !this.validator(entity);
    if (event.target.id === "classificationType") {
      this.loadClassificationsByType(event.target.value);
      entity["parent"] = undefined;
    }
    this.setState({ entity, disabled });
  };

  validator = entity => {
    return validateEntity(entity, validationRules.classificationDetail);
  };

  onSaveLoad = data => {
    if (this.state.id) {
      this.loadClassification(this.state.id);
    } else {
      const classifiactionDetailPath = PathUtil.adminItemsPath(
        { path: paths.CLASSIFICATIONS },
        { path: paths.DETAIL, id: data.id }
      );
      this.changePath(classifiactionDetailPath, data.name);
    }
  };

  saveNewEntity = () => {
    this.prepareCall(
      this.classificationService.postEntity(
        this.state.entity,
        this.onSaveLoad,
        this.messageHandler.handleError
      )
    );
    return this.performCalls();
  };

  updateEntity = entity => {
    this.prepareCall(
      this.classificationService.patchEntity(
        this.state.entity,
        this.state.oldEntity,
        this.onSaveLoad,
        this.messageHandler.handleError
      )
    );
    return this.performCalls();
  };

  getSubArraysPaths = () => {
    return [];
  };

  onEntityLoad = data => {
    this.setState({
      disabled: false,
      entity: { ...data },
      oldEntity: { ...data }
    });
    this.setComponentTitle(data.name, true);
    this.loadEntityStatus(data);
    this.loadEntityType(data);
    this.loadEntityParent(data);
  };

  componentDidMount() {
    super.componentDidMount();
    this.loadData();
    this.loadStatuses();
    this.loadAllTypes();
  }

  loadStatuses() {
    this.prepareCall(
      this.classificationStatusService.getEntities(
        activeFilterPageInfo,
        this.sortInfo,
        this.onStatusesLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  }

  onStatusesLoad = data => {
    const statuses = data._embedded ? data._embedded.content : [];
    this.setState({
      statuses: statuses
    });
  };

  loadAllTypes() {
    this.prepareCall(
      this.classificationTypeService.getEntities(
        activeFilterPageInfo,
        this.sortInfo,
        this.onAllTypesLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  }

  onAllTypesLoad = data => {
    const types = data._embedded ? data._embedded.content : [];
    this.setState({
      types: types
    });
  };

  loadData = () => {
    if (this.state.id) {
      this.loadClassification(this.state.id);
    } else {
      this.setComponentTitle(labels.classification);
    }
  };

  loadClassification = id => {
    this.prepareCall(
      this.classificationService.getEntityById(
        id,
        this.onEntityLoad,
        this.messageHandler.handleError
      )
    );
    this.performCalls();
  };

  componentDidUpdate(prevProps) {
    const newId = this.resolveItemId();
    if (newId !== PathUtil.pathParameter(prevProps, "id")) {
      this.state.id = newId;
      this.setState(this.initialState(this.state));
      this.loadData();
    }
  }

  getComponentTitle = () => {
    return this.state.entity ? this.state.entity.name : undefined;
  };

  render() {
    return (
      <ClassificationDetail
        {...this.renderProps()}
        handleOnDelete={this.handleOnDelete}
        onChange={this.handleDialogChange}
        maxLength={this.maxLength}
        disabled={this.state.disabled}
        isNew={this.state.id ? false : true}
        validationRules={validationRules.classificationDetail}
        statuses={this.state.statuses}
        types={this.state.types}
        possibleParents={this.state.possibleParents}
        {...this.handlerProps()}
      />
    );
  }
}

export default withKeycloak(ClassificationDetailContainer);
